// src/components/AriaLiveAnnouncer.jsx
// Utility component for screen reader announcements
// Used to announce dynamic content changes throughout the app

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

// Context for global announcer
const AnnouncerContext = createContext(null);

/**
 * AriaLiveAnnouncer Provider
 * 
 * Wrap your app with this to enable screen reader announcements anywhere.
 * 
 * Usage:
 * ```jsx
 * // In App.jsx
 * <AriaLiveProvider>
 *   <App />
 * </AriaLiveProvider>
 * 
 * // In any component
 * const { announce } = useAnnouncer();
 * announce('Question 1 of 5: Tell me about yourself');
 * ```
 */
export function AriaLiveProvider({ children }) {
  const [politeMessage, setPoliteMessage] = useState('');
  const [assertiveMessage, setAssertiveMessage] = useState('');
  const clearTimeoutRef = useRef(null);

  // Clear message after screen reader has time to read it
  const clearMessage = useCallback((setter) => {
    if (clearTimeoutRef.current) {
      clearTimeout(clearTimeoutRef.current);
    }
    clearTimeoutRef.current = setTimeout(() => {
      setter('');
    }, 1000);
  }, []);

  /**
   * Announce a message to screen readers
   * @param {string} message - The message to announce
   * @param {string} priority - 'polite' (default, waits for pause) or 'assertive' (immediate)
   */
  const announce = useCallback((message, priority = 'polite') => {
    if (priority === 'assertive') {
      setAssertiveMessage(message);
      clearMessage(setAssertiveMessage);
    } else {
      setPoliteMessage(message);
      clearMessage(setPoliteMessage);
    }
  }, [clearMessage]);

  /**
   * Announce page navigation/title change
   */
  const announcePageChange = useCallback((pageTitle) => {
    announce(`Navigated to ${pageTitle}`, 'polite');
  }, [announce]);

  /**
   * Announce loading state
   */
  const announceLoading = useCallback((isLoading, context = 'content') => {
    if (isLoading) {
      announce(`Loading ${context}`, 'polite');
    } else {
      announce(`${context} loaded`, 'polite');
    }
  }, [announce]);

  /**
   * Announce error
   */
  const announceError = useCallback((errorMessage) => {
    announce(`Error: ${errorMessage}`, 'assertive');
  }, [announce]);

  useEffect(() => {
    return () => {
      if (clearTimeoutRef.current) {
        clearTimeout(clearTimeoutRef.current);
      }
    };
  }, []);

  return (
    <AnnouncerContext.Provider value={{ 
      announce, 
      announcePageChange, 
      announceLoading, 
      announceError 
    }}>
      {children}
      
      {/* Polite live region - waits for pause in speech */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {politeMessage}
      </div>
      
      {/* Assertive live region - interrupts current speech */}
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {assertiveMessage}
      </div>
      
      <style jsx>{`
        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }
      `}</style>
    </AnnouncerContext.Provider>
  );
}

/**
 * Hook to access the announcer
 */
export function useAnnouncer() {
  const context = useContext(AnnouncerContext);
  
  if (!context) {
    // Return no-op functions if not wrapped in provider
    return {
      announce: () => {},
      announcePageChange: () => {},
      announceLoading: () => {},
      announceError: () => {}
    };
  }
  
  return context;
}

/**
 * Standalone AriaLive component for single-use cases
 * 
 * Usage:
 * ```jsx
 * <AriaLive message={statusMessage} />
 * <AriaLive message={errorMessage} priority="assertive" />
 * ```
 */
export function AriaLive({ message, priority = 'polite' }) {
  const [currentMessage, setCurrentMessage] = useState('');
  
  useEffect(() => {
    if (message) {
      // Clear and re-set to ensure announcement
      setCurrentMessage('');
      const timer = setTimeout(() => {
        setCurrentMessage(message);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [message]);
  
  return (
    <div
      role={priority === 'assertive' ? 'alert' : 'status'}
      aria-live={priority}
      aria-atomic="true"
      className="sr-only"
      style={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0
      }}
    >
      {currentMessage}
    </div>
  );
}

/**
 * Interview-specific announcements
 */
export const InterviewAnnouncements = {
  questionStart: (number, total, questionText) => 
    `Question ${number} of ${total}: ${questionText}`,
  
  avatarSpeaking: () => 
    'Interviewer is speaking',
  
  avatarStopped: () => 
    'Interviewer finished speaking. Your turn to respond.',
  
  listening: () => 
    'Listening for your response. Speak your answer now.',
  
  processing: () => 
    'Processing your response. Please wait.',
  
  interviewComplete: () => 
    'Interview complete. Thank you for your time.',
  
  connectionError: (error) => 
    `Connection error: ${error}. Please check your connection and try again.`,
  
  timeWarning: (minutes) => 
    `${minutes} minute${minutes !== 1 ? 's' : ''} remaining in the interview.`,
  
  progressUpdate: (current, total) => 
    `Progress: ${current} of ${total} questions completed.`
};

export default AriaLiveProvider;
